import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel
} from '@mui/material';

const AdminModels = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    provider: '',
    model_identifier: '',
    provider_api_model_name: '',
    display_label: '',
    supports_vision: false,
    is_active: true,
    max_input_tokens: '',
    max_output_tokens: '',
    pricing_prompt_token_usd: '',
    pricing_completion_token_usd: '',
    notes: ''
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error } = await supabase
      .from('llm_models')
      .select('id, provider, model_identifier, provider_api_model_name, display_label, supports_vision, is_active, max_input_tokens, max_output_tokens, pricing_prompt_token_usd, pricing_completion_token_usd, notes')
      .order('provider', { ascending: true })
      .order('display_label', { ascending: true, nullsFirst: false });
    if (error) setError(error.message);
    setModels(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({
      provider: '',
      model_identifier: '',
      provider_api_model_name: '',
      display_label: '',
      supports_vision: false,
      is_active: true,
      max_input_tokens: '',
      max_output_tokens: '',
      pricing_prompt_token_usd: '',
      pricing_completion_token_usd: '',
      notes: ''
    });
    setIsOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      provider: row.provider || '',
      model_identifier: row.model_identifier || '',
      provider_api_model_name: row.provider_api_model_name || '',
      display_label: row.display_label || '',
      supports_vision: !!row.supports_vision,
      is_active: !!row.is_active,
      max_input_tokens: row.max_input_tokens ?? '',
      max_output_tokens: row.max_output_tokens ?? '',
      pricing_prompt_token_usd: row.pricing_prompt_token_usd ?? '',
      pricing_completion_token_usd: row.pricing_completion_token_usd ?? '',
      notes: row.notes ?? ''
    });
    setIsOpen(true);
  };
  const closeDlg = () => setIsOpen(false);

  const save = async () => {
    setError('');
    const payload = {
      provider: form.provider,
      model_identifier: form.model_identifier,
      provider_api_model_name: form.provider_api_model_name || null,
      display_label: form.display_label,
      supports_vision: !!form.supports_vision,
      is_active: !!form.is_active,
      max_input_tokens: form.max_input_tokens === '' ? null : Number(form.max_input_tokens),
      max_output_tokens: form.max_output_tokens === '' ? null : Number(form.max_output_tokens),
      pricing_prompt_token_usd: form.pricing_prompt_token_usd === '' ? null : Number(form.pricing_prompt_token_usd),
      pricing_completion_token_usd: form.pricing_completion_token_usd === '' ? null : Number(form.pricing_completion_token_usd),
      notes: form.notes || null,
    };
    if (editing) {
      const { error } = await supabase.from('llm_models').update(payload).eq('id', editing.id);
      if (error) return setError(error.message);
    } else {
      const { error } = await supabase.from('llm_models').insert(payload);
      if (error) return setError(error.message);
    }
    setIsOpen(false);
    load();
  };

  const remove = async (row) => {
    setError('');
    const { error } = await supabase.from('llm_models').delete().eq('id', row.id);
    if (error) return setError(error.message);
    load();
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>Manage LLM Models</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ mb: 2 }}>
        <Button variant="contained" onClick={openNew}>Add Model</Button>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Identifier</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Vision</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Max In Tokens</TableCell>
                <TableCell align="right">Max Out Tokens</TableCell>
                <TableCell align="right">$ Prompt / 1M</TableCell>
                <TableCell align="right">$ Output / 1M</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {models.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.provider}</TableCell>
                  <TableCell>{m.model_identifier}</TableCell>
                  <TableCell>{m.display_label}</TableCell>
                  <TableCell>{m.supports_vision ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{m.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">{m.max_input_tokens ?? ''}</TableCell>
                  <TableCell align="right">{m.max_output_tokens ?? ''}</TableCell>
                  <TableCell align="right">{m.pricing_prompt_token_usd ?? ''}</TableCell>
                  <TableCell align="right">{m.pricing_completion_token_usd ?? ''}</TableCell>
                  <TableCell sx={{ maxWidth: 240 }} title={m.notes || ''}>{(m.notes || '').slice(0, 60)}{m.notes && m.notes.length > 60 ? '…' : ''}</TableCell>
                  <TableCell align="right">
                    <Button size="small" sx={{ mr: 1 }} onClick={() => openEdit(m)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => remove(m)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={isOpen} onClose={closeDlg} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Model' : 'Add Model'}</DialogTitle>
        <DialogContent>
          <TextField margin="normal" fullWidth label="Provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} />
          <TextField margin="normal" fullWidth label="Model Identifier" value={form.model_identifier} onChange={(e) => setForm({ ...form, model_identifier: e.target.value })} />
          <TextField margin="normal" fullWidth label="Provider API Model Name (optional)" helperText="Override identifier sent to provider (leave blank to use identifier)." value={form.provider_api_model_name} onChange={(e) => setForm({ ...form, provider_api_model_name: e.target.value })} />
          <TextField margin="normal" fullWidth label="Display Label" value={form.display_label} onChange={(e) => setForm({ ...form, display_label: e.target.value })} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField type="number" margin="normal" label="Max Input Tokens" value={form.max_input_tokens} onChange={(e) => setForm({ ...form, max_input_tokens: e.target.value })} fullWidth />
            <TextField type="number" margin="normal" label="Max Output Tokens" value={form.max_output_tokens} onChange={(e) => setForm({ ...form, max_output_tokens: e.target.value })} fullWidth />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField type="number" margin="normal" label="Pricing $/1M Prompt" value={form.pricing_prompt_token_usd} onChange={(e) => setForm({ ...form, pricing_prompt_token_usd: e.target.value })} fullWidth />
            <TextField type="number" margin="normal" label="Pricing $/1M Output" value={form.pricing_completion_token_usd} onChange={(e) => setForm({ ...form, pricing_completion_token_usd: e.target.value })} fullWidth />
          </Box>
          <TextField margin="normal" fullWidth label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline rows={3} />
          <FormControlLabel control={<Switch checked={form.supports_vision} onChange={(e) => setForm({ ...form, supports_vision: e.target.checked })} />} label="Supports Vision" />
          <FormControlLabel control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDlg}>Cancel</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminModels;
